// Sprint 2 PR-D2 — Central Brain Approvals view.
// Faz 48 PR-D (2026-05-12) — added per-row approve/reject buttons for pending rows.
// Endpoint: GET /api/v2/approvals (LEFT JOIN actions),
//           POST /api/v2/approvals/:id/{approve|reject}
// CHARTER § 7.x — Yılmaz can act on pending central_brain.approvals without
// leaving the dashboard.

import { escHtml } from '../utils.js';
import { apiGetJson } from '../api.js';

export interface ApprovalRow {
  id: string;
  tenantId: string;
  actionId: string;
  approvalType: string;
  reason: string | null;
  status: string;
  // Faz 50 M6 — P1 Approval Executor uyumu.
  executorStatus: 'pending' | 'success' | 'failed' | 'skipped' | null;
  requestedByAgent: string | null;
  approvedBy: string | null;
  createdAt: string;
  resolvedAt: string | null;
  action: { actionType: string; title: string; status: string; riskLevel: string } | null;
}

export interface ApprovalsSummary {
  total: number;
  byStatus: Record<string, number>;
  byApprovalType: Record<string, number>;
  // Faz 50 M6 — P1 executor breakdown + actionable pending sayacı.
  byExecutorStatus?: Record<string, number>;
  actionablePending?: number;
}
interface ApprovalsResponse { ok: true; approvals: ApprovalRow[]; summary: ApprovalsSummary; }
export interface ApprovalsFilters { status?: string; actionId?: string; approvalType?: string; tenantId?: string; limit?: number; }

type ViewState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; approvals: ApprovalRow[]; summary: ApprovalsSummary; filters: ApprovalsFilters }
  | { kind: 'error'; message: string; filters: ApprovalsFilters };

let state: ViewState = { kind: 'idle' };
let filters: ApprovalsFilters = { limit: 100 };

export async function loadCbApprovals(): Promise<void> {
  state = { kind: 'loading' };
  const r = await apiGetJson<ApprovalsResponse>('/api/v2/approvals', filters as Record<string, string | number | undefined>);
  if (r.ok) state = { kind: 'ready', approvals: r.data.approvals, summary: r.data.summary, filters: { ...filters } };
  else state = { kind: 'error', message: r.message ?? r.error, filters: { ...filters } };
}

export function renderCbApprovals(): void {
  const root = document.getElementById('view-cb-approvals');
  if (!root) return;
  root.innerHTML = renderCbApprovalsHtml(state);
  attachHandlers();
}

export function renderCbApprovalsHtml(s: ViewState): string {
  const f = (s.kind === 'ready' || s.kind === 'error') ? s.filters : filters;
  const header = `
    <div class="view-header">
      <div>
        <div class="view-title">Approvals</div>
        <div class="view-sub">central_brain.approvals + LEFT JOIN actions — pending kayıtlar onaylanabilir (Faz 48 PR-D)</div>
      </div>
    </div>
    <div class="rt-card" style="display:flex;gap:8px;flex-wrap:wrap;align-items:flex-end;">
      ${selectFilter('status', 'Status', f.status, ['pending','approved','rejected','expired'])}
      ${inputFilter('approvalType', 'Type', f.approvalType)}
      ${inputFilter('actionId', 'Action ID', f.actionId)}
      ${inputFilter('tenantId', 'Tenant', f.tenantId)}
      <button id="cbap-apply" class="btn-primary" style="padding:6px 12px;">Filtrele</button>
    </div>`;
  if (s.kind === 'idle' || s.kind === 'loading') return `${header}<div class="empty-state"><div class="empty-icon">⏳</div><div class="empty-title">Yükleniyor…</div></div>`;
  if (s.kind === 'error') return `${header}<div class="rt-card" style="border-left:3px solid var(--danger,#e55);"><div class="rt-card-title">Yüklenemedi</div><div style="color:var(--muted);font-size:13px;">${escHtml(s.message)}</div></div>`;
  const { approvals, summary } = s;
  // Faz 50 M6 — "X/Y bekleyen" sayacı: X=actionable pending, Y=total.
  // Eski "0/43" görüntüsü (43 total, 0 actionable) yerine artık doğru sayı
  // gösterilir; Onaylar widget'i ile sync (her ikisi de actionablePending'i
  // gösterir).
  const actionable = summary.actionablePending ?? summary.byStatus['pending'] ?? 0;
  return `${header}
    <div class="rt-card" style="display:flex;flex-wrap:wrap;gap:14px;font-size:12px;">
      <span><strong>${actionable}</strong>/<strong>${summary.total}</strong> bekleyen onay</span>
      ${chips('status', summary.byStatus)}
      ${summary.byExecutorStatus ? chips('executor', summary.byExecutorStatus) : ''}
      ${chips('type', summary.byApprovalType)}
    </div>
    <div class="rt-card">
      <div class="rt-card-title">Approvals (${approvals.length})</div>
      ${approvals.length === 0
        ? '<div class="empty-state"><div class="empty-icon">📭</div><div class="empty-title">Eşleşen approval yok</div></div>'
        : `<table style="width:100%;font-size:13px;">
            <thead><tr>
              <th style="text-align:left;">ID</th><th style="text-align:left;">Type</th>
              <th style="text-align:left;">Action</th><th style="text-align:left;">Status</th>
              <th style="text-align:left;">Executor</th>
              <th style="text-align:left;">Requested By</th><th style="text-align:left;">Reason</th>
              <th style="text-align:left;">Created</th>
              <th style="text-align:right;">Aksiyon</th>
            </tr></thead>
            <tbody>${approvals.map((a) => `
              <tr data-approval-id="${escHtml(a.id)}">
                <td>${escHtml(a.id.slice(0,8))}</td>
                <td><code style="font-size:11px;">${escHtml(a.approvalType)}</code></td>
                <td>${a.action
                    ? `<span title="${escHtml(a.action.title)}" style="background:var(--bg2);padding:2px 6px;border-radius:4px;font-size:11px;"><code>${escHtml(a.action.actionType)}</code> · ${escHtml(a.action.status)} · ${escHtml(a.action.riskLevel)}</span>`
                    : '<span style="color:var(--danger,#e55);font-size:11px;">action silinmiş</span>'}</td>
                <td>${escHtml(a.status)}</td>
                <td><code style="font-size:11px;">${escHtml(a.executorStatus ?? '—')}</code></td>
                <td>${escHtml(a.requestedByAgent ?? '—')}</td>
                <td style="font-size:11px;color:var(--muted);max-width:280px;overflow:hidden;text-overflow:ellipsis;">${escHtml((a.reason ?? '').slice(0,120))}</td>
                <td style="color:var(--muted);font-size:12px;">${escHtml(a.createdAt)}</td>
                <td style="text-align:right;white-space:nowrap;">${
                  a.status === 'pending'
                    ? `<button class="cbap-approve" data-id="${escHtml(a.id)}" style="background:#16a34a;color:white;border:0;padding:3px 8px;border-radius:4px;cursor:pointer;margin-right:4px;font-size:11px;">✓ Onayla</button>
                       <button class="cbap-reject" data-id="${escHtml(a.id)}" style="background:#dc2626;color:white;border:0;padding:3px 8px;border-radius:4px;cursor:pointer;font-size:11px;">✗ Reddet</button>`
                    : a.resolvedAt
                      ? `<span style="color:var(--muted);font-size:11px;">${escHtml(a.approvedBy ?? '—')}</span>`
                      : '—'
                }</td>
              </tr>`).join('')}
            </tbody>
          </table>`
      }
    </div>`;
}

function chips(label: string, m: Record<string, number>): string {
  return `<span><span style="color:var(--muted);">${escHtml(label)}:</span> ${Object.entries(m).map(([k,v]) => `<span style="background:var(--bg2);padding:2px 6px;border-radius:8px;margin-right:4px;">${escHtml(k)} <strong>${v}</strong></span>`).join('')}</span>`;
}
function selectFilter(id: string, label: string, value: string | undefined, opts: string[]): string {
  return `<label style="font-size:11px;color:var(--muted);display:flex;flex-direction:column;gap:2px;">${escHtml(label)}<select id="cbap-f-${id}" class="form-select" style="width:130px;padding:4px 8px;"><option value="">(any)</option>${opts.map(o => `<option value="${escHtml(o)}"${o===value?' selected':''}>${escHtml(o)}</option>`).join('')}</select></label>`;
}
function inputFilter(id: string, label: string, value: string | undefined): string {
  return `<label style="font-size:11px;color:var(--muted);display:flex;flex-direction:column;gap:2px;">${escHtml(label)}<input id="cbap-f-${id}" class="form-input" value="${escHtml(value ?? '')}" style="width:140px;padding:4px 8px;" /></label>`;
}
function attachHandlers(): void {
  const btn = document.getElementById('cbap-apply');
  if (btn) {
    btn.addEventListener('click', async () => {
      const getV = (id: string) => (document.getElementById(`cbap-f-${id}`) as HTMLInputElement | HTMLSelectElement | null)?.value || undefined;
      filters = { status: getV('status'), approvalType: getV('approvalType'), actionId: getV('actionId'), tenantId: getV('tenantId'), limit: 100 };
      await loadCbApprovals();
      renderCbApprovals();
    });
  }
  // Faz 48 PR-D — approve/reject buttons (event delegation on view root).
  const root = document.getElementById('view-cb-approvals');
  if (!root || (root as HTMLElement & { __cbapBound?: boolean }).__cbapBound) return;
  (root as HTMLElement & { __cbapBound?: boolean }).__cbapBound = true;
  root.addEventListener('click', async (ev) => {
    const target = ev.target as HTMLElement | null;
    if (!target) return;
    const approve = target.closest('.cbap-approve') as HTMLButtonElement | null;
    const reject = target.closest('.cbap-reject') as HTMLButtonElement | null;
    const btn = approve || reject;
    if (!btn) return;
    const id = btn.dataset.id;
    if (!id) return;
    const action: 'approve' | 'reject' = approve ? 'approve' : 'reject';
    const label = action === 'approve' ? 'onayla' : 'reddet';
    if (!confirm(`Bu approval ${label}ansın mı? id=${id.slice(0,8)}`)) return;
    btn.disabled = true;
    btn.textContent = '…';
    try {
      const r = await fetch(`/api/v2/approvals/${id}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ note: `dashboard /onaylar nav (Faz 48 PR-D)` }),
      });
      if (!r.ok) {
        const t = await r.text().catch(() => 'HTTP ' + r.status);
        alert(`${label} başarısız: ${t.slice(0, 200)}`);
        btn.disabled = false;
        btn.textContent = action === 'approve' ? '✓ Onayla' : '✗ Reddet';
        return;
      }
      await loadCbApprovals();
      renderCbApprovals();
    } catch (e) {
      alert(`${label} hatası: ` + (e as Error).message);
      btn.disabled = false;
      btn.textContent = action === 'approve' ? '✓ Onayla' : '✗ Reddet';
    }
  });
}
